import React, { useEffect, useState } from 'react';
import { Link, useParams, Navigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { getMarketConfig } from './marketConfig';
import {
  QeukHeroAttribution,
  QeukTrustNudge,
  QeukPaymentPlaceholder,
  QeukFooter,
} from './QeukAttribution';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const APP_BASE = process.env.REACT_APP_APP_BASE_URL || 'https://geniusoneplus.com';

// Sprint 10.9 + 11 — Reusable market-expansion waitlist landing page.
// Sprint 11 (Task I) adds a post-signup queue-position card + "Share to jump
// the queue" WhatsApp referral loop.
export default function WaitlistLanding({ slug: slugProp }) {
  const params = useParams();
  const [searchParams] = useSearchParams();
  const market = slugProp || params.market;
  const cfg = getMarketConfig(market);
  const { t } = useTranslation();
  const [wa, setWa] = useState('');
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(null);
  const [signup, setSignup] = useState(null);        // POST response
  const [positionInfo, setPositionInfo] = useState(null);  // refreshed via GET
  const [queueSize, setQueueSize] = useState(null);  // Task K — for invite banner math
  const referrerCode = searchParams.get('ref');

  // Auto-switch to Urdu on /pk so RTL kicks in (Task J). Users can still
  // manually change UI language later. Handles common English detector
  // variants (`en`, `en-US`, even the odd `en-US@posix` some browsers report).
  const { i18n } = useTranslation();
  useEffect(() => {
    if (market !== 'pk') return;
    const current = (i18n.language || '').toLowerCase();
    const isEnglishish = current === 'en' || current.startsWith('en-') || current.startsWith('en_') || !current;
    if (isEnglishish) i18n.changeLanguage('ur');
  }, [market, i18n]);

  // Refresh the position info a couple of seconds after successful signup
  // so any referrer credit that fired during POST is reflected.
  useEffect(() => {
    if (!signup?.referral_code) return;
    const load = async () => {
      try {
        const r = await axios.get(
          `${API}/waitlist/${cfg.slug}/position/${signup.referral_code}`,
        );
        setPositionInfo(r.data);
      } catch (_e) { /* silent — non-critical */ }
    };
    load();
    const t = setInterval(load, 15000); // keep the queue counter live-ish
    return () => clearInterval(t);
  }, [signup, cfg]);

  // Sprint 11 (Task K) — fetch live queue size on mount so we can show
  // invitees the math: "You'll join at #(N-10) instead of #N".
  useEffect(() => {
    if (!cfg) return;
    axios.get(`${API}/waitlist/${cfg.slug}/count`)
      .then(r => setQueueSize(r.data.waitlist_size))
      .catch(() => setQueueSize(null));
  }, [cfg]);

  if (!cfg) return <Navigate to="/" replace />;

  const badgeLabel = t('waitlist.eta.coming_2027') || 'Coming 2027';

  const submit = async (e) => {
    e.preventDefault();
    setStatus('loading');
    setError(null);
    try {
      const digits = wa.replace(/[^\d]/g, '');
      const cc = cfg.countryCode;
      const number = digits.startsWith(cc)
        ? `+${digits}`
        : `+${cc}${digits.replace(/^0/, '')}`;
      const body = { whatsapp: number, source: `landing_${cfg.slug}` };
      if (referrerCode) body.referrer_code = referrerCode;
      const r = await axios.post(`${API}/waitlist/${cfg.slug}`, body);
      setSignup(r.data);
      setStatus('done');
    } catch (err) {
      setStatus('error');
      setError(err.response?.data?.detail || 'Please enter a valid international WhatsApp number.');
    }
  };

  const shareToJump = () => {
    if (!signup?.referral_code) return;
    const url = `${APP_BASE}/${cfg.slug}?ref=${signup.referral_code}`;
    const msg = encodeURIComponent(
      `I'm on the Genius One Plus ${cfg.nativeName} waitlist! Kids 10-12 learn English + AI. Join me and skip ahead: ${url}`,
    );
    window.open(`https://wa.me/?text=${msg}`, '_blank', 'noopener');
  };

  const isRTL = market === 'pk' && (i18n.language || '').split(/[-_@]/)[0].toLowerCase() === 'ur';

  return (
    <div
      className={`min-h-screen bg-gradient-to-br ${cfg.palette}`}
      data-testid={`${cfg.testidPrefix}-landing`}
      dir={isRTL ? 'rtl' : 'ltr'}
    >
      <div className="max-w-6xl mx-auto px-4 py-8">
        <nav className="flex justify-between items-center mb-8">
          <div>
            <p className="text-xs text-muted-foreground uppercase">Genius One Plus</p>
            <p className="text-lg font-bold">
              {cfg.flag} {cfg.nativeName}
            </p>
          </div>
          <Link to="/" className="text-sm underline" data-testid={`${cfg.testidPrefix}-back-link`}>
            &larr; Back to Sri Lanka site
          </Link>
        </nav>

        {referrerCode && !signup && (
          <div
            className="mb-6 rounded-xl border-2 border-amber-300 bg-amber-50 p-4 text-amber-900"
            data-testid={`${cfg.testidPrefix}-invite-banner`}
          >
            <p className="font-semibold">
              🎁 You&rsquo;re being invited by a friend!
            </p>
            {queueSize != null && queueSize > 0 ? (
              <p className="text-sm mt-1" data-testid={`${cfg.testidPrefix}-invite-banner-math`}>
                Sign up now &mdash; you&rsquo;ll join at approx{' '}
                <span className="font-semibold">
                  #{Math.max(1, (queueSize + 1) - 10)}
                </span>{' '}
                instead of #{queueSize + 1}. That&rsquo;s{' '}
                <span className="font-semibold">10 places ahead</span>. Your
                friend&rsquo;s queue slot also moves up 10 places.
              </p>
            ) : (
              <p className="text-sm mt-1">
                Sign up now to jump 10 places ahead. Your friend&rsquo;s queue
                slot also moves up 10 places.
              </p>
            )}
          </div>
        )}

        {/* Task K bonus — friend-awareness banner for first-time visitors
            (no ?ref). Educates every visitor that referrals give a real
            10-slot boost, which should lift ask-back / share-back rates. */}
        {!referrerCode && !signup && (
          <div
            className="mb-6 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900"
            data-testid={`${cfg.testidPrefix}-friend-awareness-banner`}
          >
            💡 <span className="font-semibold">Have a friend already on the list?</span>{' '}
            Ask them to send you their invite link &mdash; you&rsquo;ll skip 10 places ahead.
          </div>
        )}

        <div className="grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <span
              className={`inline-block px-3 py-1 rounded-full text-xs font-semibold mb-4 ${cfg.accent}`}
              data-testid={`${cfg.testidPrefix}-badge`}
            >
              {badgeLabel}
            </span>
            <h1 className="text-4xl md:text-5xl font-bold mb-4"
                data-testid={`${cfg.testidPrefix}-hero-title`}>
              {cfg.hero.title}
            </h1>
            <QeukHeroAttribution testidPrefix={cfg.testidPrefix} />
            <p className="text-lg text-muted-foreground mb-6"
               data-testid={`${cfg.testidPrefix}-hero-sub`}>
              {cfg.hero.sub}
            </p>

            {status === 'done' && signup ? (
              <QueueCard
                cfg={cfg}
                signup={signup}
                positionInfo={positionInfo}
                onShare={shareToJump}
              />
            ) : (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">{cfg.waitlistTitle}</CardTitle>
                </CardHeader>
                <CardContent>
                  <form onSubmit={submit} className="flex flex-col sm:flex-row gap-2">
                    <Input
                      placeholder={cfg.phonePlaceholder}
                      value={wa}
                      onChange={(e) => setWa(e.target.value)}
                      data-testid={`${cfg.testidPrefix}-waitlist-input`}
                      required
                    />
                    <Button type="submit" disabled={status === 'loading'}
                            data-testid={`${cfg.testidPrefix}-waitlist-submit`}>
                      {status === 'loading' ? 'Sending\u2026' : 'Notify me'}
                    </Button>
                  </form>
                  {status === 'error' && (
                    <p className="text-xs text-red-600 mt-2"
                       data-testid={`${cfg.testidPrefix}-waitlist-error`}>
                      {typeof error === 'string' ? error : 'Please enter a valid WhatsApp number.'}
                    </p>
                  )}
                  <p className="text-xs text-muted-foreground mt-3">
                    {cfg.pricingHint}
                  </p>
                  <QeukTrustNudge testidPrefix={cfg.testidPrefix} />
                </CardContent>
              </Card>
            )}
            <QeukPaymentPlaceholder testidPrefix={cfg.testidPrefix} />
          </div>

          <div className="relative">
            <div className="absolute inset-0 bg-slate-200/40 rounded-3xl -rotate-2" />
            <img src={cfg.heroImage} alt="Children exploring AI"
                 className="relative rounded-3xl shadow-2xl w-full h-auto object-cover"
                 data-testid={`${cfg.testidPrefix}-hero-img`} />
          </div>
        </div>

        <div className="mt-16 text-center text-xs text-muted-foreground">
          <p>
            Full localisation for this market is on the way. Waitlist form
            captures your WhatsApp number so we can notify you the moment we
            launch. No payments taken yet.
          </p>
          <QeukFooter testidPrefix={cfg.testidPrefix} />
        </div>
      </div>
    </div>
  );
}

// --------------------------------------------------------------------- //
// Post-signup queue card
// --------------------------------------------------------------------- //
function QueueCard({ cfg, signup, positionInfo, onShare }) {
  const info = positionInfo || signup;
  const slot = info.queue_slot ?? info.position;
  const total = info.total_in_queue ?? signup.waitlist_size ?? '\u2014';
  const jumpsUsed = info.jumps_used ?? 0;
  const referralCount = info.referral_count ?? 0;
  const jumpsLeft = info.jumps_left_this_month ?? 3;
  // Task K — invitee bonus is only present on the *fresh* signup response
  const inviteeBonus = signup.invitee_bonus;
  const gotBonus = inviteeBonus && inviteeBonus.applied && inviteeBonus.places > 0;

  return (
    <Card className="border-2 border-emerald-400 shadow-lg"
          data-testid={`${cfg.testidPrefix}-queue-card`}>
      <CardHeader>
        <CardTitle className="text-2xl">
          🎉 You&rsquo;re #<span data-testid={`${cfg.testidPrefix}-queue-slot`}>{slot}</span>{' '}
          on the {cfg.nativeName} waitlist
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {gotBonus && (
          <div
            className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900"
            data-testid={`${cfg.testidPrefix}-invitee-bonus-note`}
          >
            🎁 You jumped{' '}
            <span className="font-semibold">{inviteeBonus.places} places</span>{' '}
            thanks to a friend&rsquo;s invite (would have been #{signup.position}).
          </div>
        )}
        {signup.already_registered && (
          <p className="text-xs text-slate-500" data-testid={`${cfg.testidPrefix}-already-registered`}>
            (This WhatsApp number was already on the list — welcome back!)
          </p>
        )}
        <p className="text-sm text-slate-700">
          Share to jump 10 more places 👇
        </p>
        <Button size="lg" onClick={onShare}
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold"
                data-testid={`${cfg.testidPrefix}-share-jump-btn`}>
          Share on WhatsApp 📱
        </Button>
        <div className="text-xs text-slate-600 pt-2 space-y-1"
             data-testid={`${cfg.testidPrefix}-jump-stats`}>
          {jumpsUsed > 0 ? (
            <p>
              You&rsquo;ve jumped{' '}
              <span className="font-semibold text-emerald-700">{jumpsUsed * 10} places</span>{' '}
              ({referralCount} friend{referralCount === 1 ? '' : 's'} joined)
              &nbsp;·&nbsp; <span className="font-semibold">{jumpsLeft} jumps left this month</span>
            </p>
          ) : (
            <p>
              No jumps yet — you have{' '}
              <span className="font-semibold text-emerald-700">{jumpsLeft} jumps left this month</span>.
            </p>
          )}
          <p className="text-slate-400">
            Total in queue: <span data-testid={`${cfg.testidPrefix}-total-count`}>{total}</span>{' '}
            &middot; Original position: #{info.position}
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
